import * as React from 'react';
import noop from 'lodash/noop';

import {showDialog} from '../state';
import type {DialogProps} from '../types';
import {CancelButton, Dialog, SecondaryButton, SubmitButton} from './shared';
import type {ButtonType, IconType, Size, BaseButtonType} from './shared';

type ConfirmConfig = {
  // If provided, only the `submit` prop is rendered.
  noCancel?: boolean;
  cancel?: BaseButtonType;
  content: React.ReactNode;
  footerContent?: React.ReactNode;
  contentStyle?: React.CSSProperties;
  iconType?: IconType;
  onCancel?: () => void;
  onSecondary?: () => void;
  onSubmit?: () => void;
  onCloseComplete?: () => void;
  secondary?: ButtonType;
  size?: Size;
  submit?: ButtonType;
  ['data-automation-id']?: string;
  ['data-wf-modal-id']?: string;
  // The React.Element type acts as an escape hatch
  // for Logic to implement breadcrumbs.
  title: string | React.ReactElement;
  titleDataAutomationId?: string;
  style?: React.CSSProperties;
};

type ConfirmDialogProps = ConfirmConfig & {
  onCancel: () => void;
  onSecondary?: DialogProps['onSecondary'];
  onSubmit: () => void;
  style?: React.CSSProperties;
};

export const ConfirmDialog = React.memo<ConfirmDialogProps>(function Confirm({
  noCancel = false,
  cancel = {
    label: 'Cancel',
    intent: 'default',
    'data-automation-id': undefined,
  },
  content,
  contentStyle,
  iconType,
  onCancel,
  onCloseComplete,
  onSecondary,
  onSubmit,
  size,
  secondary,
  footerContent,
  submit = {
    label: 'Submit',
    intent: 'primary',
    disabled: false,
    'data-automation-id': undefined,
  },
  title,
  titleDataAutomationId,
  'data-automation-id': dataAutomationId,
  'data-wf-modal-id': wfModalId,
  style,
}) {
  return (
    <Dialog
      data-automation-id={dataAutomationId}
      data-wf-modal-id={wfModalId}
      content={content}
      contentStyle={contentStyle}
      iconType={iconType}
      onClose={onCancel}
      onCloseComplete={onCloseComplete}
      size={size}
      title={title}
      titleDataAutomationId={titleDataAutomationId}
      footerContent={footerContent}
      allowCancel={!noCancel}
      buttons={
        <>
          {secondary && (
            <SecondaryButton
              disabled={secondary.disabled}
              danger={secondary.intent === 'danger'}
              data-automation-id={
                secondary['data-automation-id'] || secondary.label
              }
              onClick={onSecondary}
              primary={secondary.intent === 'primary'}
            >
              {secondary.label}
            </SecondaryButton>
          )}
          {!noCancel && (
            <CancelButton
              danger={cancel.intent === 'danger'}
              data-automation-id={cancel['data-automation-id'] || cancel.label}
              onClick={() => {
                onCancel();
                onCloseComplete?.();
              }}
              primary={cancel.intent === 'primary'}
            >
              {cancel.label}
            </CancelButton>
          )}
          <SubmitButton
            disabled={submit.disabled}
            danger={submit.intent === 'danger'}
            data-automation-id={submit['data-automation-id'] || submit.label}
            onClick={onSubmit}
            primary={submit.intent === 'primary'}
          >
            {submit.label}
          </SubmitButton>
        </>
      }
      style={style}
    />
  );
});

export const confirm = ({
  onCancel = noop,
  onSecondary = noop,
  onSubmit = noop,
  ...staticProps
}: ConfirmConfig) =>
  showDialog(function ConfirmWrapper({
    onCancel: internalCancel,
    onSecondary: internalSecondary,
    onSubmit: internalSubmit,
  }) {
    const handleCancel = React.useCallback(() => {
      onCancel();
      internalCancel();
    }, [internalCancel]);

    const handleSecondary = React.useCallback(() => {
      onSecondary();
      internalSecondary();
    }, [internalSecondary]);

    const handleSubmit = React.useCallback(() => {
      onSubmit();
      internalSubmit();
    }, [internalSubmit]);

    return (
      <ConfirmDialog
        {...staticProps}
        onCancel={handleCancel}
        onSecondary={handleSecondary}
        onSubmit={handleSubmit}
      />
    );
  });
